// What happens to a Figma link at the moment it is pasted into the link field.
//
// Shared by Figmafolio's link field (ViewportEditor) and, later, Aero's. It never
// touches a link that is already a good /proto/ link, and it never changes how a
// link is wrapped for the iframe: that stays in editUrl (FolioForm) / editFigmaUrl.
//
//   classifyFigmaLink(raw)   what kind of link is this?
//   convertPastedLink(raw)   turn a design / file / branch link into a prototype
//                            link, and say whether anything changed
//   protoFileKey(link)       the file key of a /proto/ link, for the sharing check
//
// Rules, each tested against a real file (Testing File, 3 Nov 2026):
//   - A design link carries no scaling, so a converted link gets scaling=scale-down-width.
//     Without it the site is scaled differently from a pasted prototype link, whose
//     min-zoom / contain / scale-down are all rewritten to fit-to-width on save.
//   - A page-only link (node-id=0-1, nothing selected) gets no start point. On a direct
//     link a page start point loads a black screen. With no frame at all Figma opens the
//     file's first flow, or its first frame when the file has no flows.
import { cleanFigmaLink } from './figmaEmbedUrl';

export const DEFAULT_SCALING = 'scale-down-width';

const FIGMA_HOST = /(^|\.)figma\.com$/i;
// Figma file keys are exactly 22 letters and digits. Exactly, so a link that is still being
// typed (/proto/ABCDEFGHIJ...) is never sent to the sharing check.
const FILE_KEY = /^[A-Za-z0-9]{22}$/;
// Not prototypes: whiteboards, Make, slides, sites, buzz, community pages and so on.
const UNSUPPORTED_KINDS = ['board', 'make', 'slides', 'site', 'sites', 'buzz', 'community', 'files', 'jam'];

function parse(raw) {
  const text = (raw || '').trim();
  if (!text) return null;
  try {
    return new URL(/^https?:\/\//i.test(text) ? text : 'https://' + text);
  } catch (e) {
    return null;
  }
}

// -> 'empty' | 'not_figma' | 'unsupported' | 'proto' | 'embed' | 'design'
// 'design' means a link we know how to turn into a prototype link (design, file, branch,
// embed.figma.com/design). 'embed' is Figmafolio's own wrapped form (figma.com/embed?url=).
export function classifyFigmaLink(raw) {
  const text = (raw || '').trim();
  if (!text) return 'empty';
  const u = parse(text);
  if (!u || !FIGMA_HOST.test(u.hostname)) return 'not_figma';
  const parts = u.pathname.split('/').filter(Boolean);
  const kind = (parts[0] || '').toLowerCase();
  if (kind === 'embed' && u.searchParams.get('url')) return 'embed';
  if (kind === 'proto' && parts.length >= 2) return 'proto';
  if (['design', 'file'].includes(kind) && parts.length >= 2) return 'design';
  // embed.figma.com/proto/... and embed.figma.com/design/...
  if (u.hostname.toLowerCase().startsWith('embed.') && ['proto', 'design', 'file'].includes(kind) && parts.length >= 2) {
    return kind === 'proto' ? 'proto' : 'design';
  }
  if (UNSUPPORTED_KINDS.includes(kind)) return 'unsupported';
  return 'unsupported';
}

function isPageId(id) {
  // A page ("canvas") node id starts with 0: 0:1, 0-1, 0:12. Frames never do.
  return typeof id === 'string' && /^0[-:]/.test(id);
}

// Turn the link into a prototype link. Returns { link, changed, kind }.
// A link that is already good comes back byte for byte (changed: false).
export function convertPastedLink(raw) {
  const original = (raw || '').trim();
  const kind = classifyFigmaLink(original);
  if (kind !== 'design' && kind !== 'embed') return { link: original, changed: false, kind };

  // cleanFigmaLink reads the first path segment case-sensitively; Figma does not.
  let source = original;
  const parsed = parse(original);
  if (parsed && kind === 'design') {
    const segs = parsed.pathname.split('/');
    if (segs[1]) segs[1] = segs[1].toLowerCase();
    parsed.pathname = segs.join('/');
    source = parsed.toString();
  }
  let link = cleanFigmaLink(source);
  if (!link || link === original) return { link: original, changed: false, kind };

  const u = parse(link);
  if (u && /\/proto\//.test(u.pathname) && FIGMA_HOST.test(u.hostname) && !/\/embed$/.test(u.pathname)) {
    const node = u.searchParams.get('node-id');
    if (isPageId(node) || isPageId(u.searchParams.get('starting-point-node-id'))) {
      u.searchParams.delete('node-id');
      u.searchParams.delete('starting-point-node-id');
    }
    if (kind === 'design' && !u.searchParams.has('scaling')) u.searchParams.set('scaling', DEFAULT_SCALING);
    // cleanFigmaLink writes colons unescaped (starting-point-node-id=1:2). Keep that.
    const qs = u.searchParams.toString().replace(/%3A/gi, ':');
    link = u.origin + u.pathname + (qs ? '?' + qs : '');
  }
  return { link, changed: link !== original, kind };
}

// The file key of a /proto/ link ("KYngia..."), or null. Reads saved (wrapped) links too.
// Branch links already point at the branch's own key after conversion.
export function protoFileKey(link, depth = 0) {
  const u = parse(link);
  if (!u || !FIGMA_HOST.test(u.hostname)) return null;
  const parts = u.pathname.split('/').filter(Boolean);
  // A saved link is Figmafolio's wrapped form: figma.com/embed?...&url=<the prototype link>
  if (parts[0] === 'embed' && depth < 1) {
    const inner = u.searchParams.get('url');
    return inner ? protoFileKey(inner, depth + 1) : null;
  }
  if (parts[0] !== 'proto') return null;
  return FILE_KEY.test(parts[1] || '') ? parts[1] : null;
}

// The file's name as it reads in the link ("Testing-File" becomes "Testing File"), for the share
// guide's picture. A link carries a lossy version of the name, so this is only ever a label; null
// when there is none worth showing.
export function figmaFileName(link, depth = 0) {
  const u = parse(link);
  if (!u || !FIGMA_HOST.test(u.hostname)) return null;
  const parts = u.pathname.split('/').filter(Boolean);
  if (parts[0] === 'embed' && depth < 1) {
    const inner = u.searchParams.get('url');
    return inner ? figmaFileName(inner, depth + 1) : null;
  }
  if (parts[0] !== 'proto' || !FILE_KEY.test(parts[1] || '') || !parts[2]) return null;
  let slug;
  try {
    slug = decodeURIComponent(parts[2]);
  } catch (e) {
    return null;
  }
  const name = slug.replace(/[-_+]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!name || /^untitled$/i.test(name)) return null;
  return name.length > 30 ? name.slice(0, 29).trimEnd() + '\u2026' : name;
}
